import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { Button } from '../ui/button';
import { DELETE_BULLETIN_POST, ADD_REACTION, REMOVE_REACTION } from '../../graphql/bulletinBoard';
import { EditPostModal } from './EditPostModal';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import toast from 'react-hot-toast';

interface PostImage {
  id: string;
  url: string;
  thumbnailUrl?: string;
  width?: number;
  height?: number;
  size?: number;
}

interface BulletinAttachment {
  id: string;
  url: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedAt: string;
  isImage: boolean;
}

interface PostReactions {
  thumbsUp: string[];
  heart: string[];
  party: string[];
  eyes: string[];
}

interface BulletinPost {
  id: string;
  content: string;
  audienceType: string;
  creatorId: string;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  targetedUserIds?: string[];
  priority: string;
  isPinned: boolean;
  images: PostImage[];
  attachments?: BulletinAttachment[];
  reactions: PostReactions;
}

interface PostDetailModalProps {
  post: BulletinPost;
  isOpen: boolean;
  onClose: () => void;
  onPostUpdated: () => void;
  onPostDeleted: () => void;
  currentUserId?: string;
  currentUserRole?: string;
  onReactionUpdate?: () => void;
}

export function PostDetailModal({
  post,
  isOpen,
  onClose,
  onPostUpdated,
  onPostDeleted,
  currentUserId,
  currentUserRole,
  onReactionUpdate,
}: PostDetailModalProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const [deletePost, { loading: deleting }] = useMutation<{ deleteBulletinPost: any }>(DELETE_BULLETIN_POST);
  const [addReaction] = useMutation<{ addReaction: any }>(ADD_REACTION);
  const [removeReaction] = useMutation<{ removeReaction: any }>(REMOVE_REACTION);

  if (!isOpen || !post) return null;

  const isCreator = currentUserId === post.creatorId;
  const isAdmin = currentUserRole === 'ADMIN';
  const canEdit = isCreator || isAdmin;
  const canDelete = isCreator || isAdmin;

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAudienceType = (audienceType: string): string => {
    return audienceType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getAudienceBadgeColor = (audienceType: string): string => {
    switch (audienceType) {
      case 'ALL_USERS':
        return 'bg-indigo-100 text-indigo-800';
      case 'ALL_STAFF':
        return 'bg-blue-100 text-blue-800';
      case 'ALL_CLIENTS':
        return 'bg-green-100 text-green-800';
      case 'SPECIFIC_STAFF':
        return 'bg-purple-100 text-purple-800';
      case 'SPECIFIC_CLIENTS':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return {
          color: 'bg-red-100 text-red-800 border-red-200',
          icon: '🚨',
          label: 'Urgent',
        };
      case 'IMPORTANT':
        return {
          color: 'bg-amber-100 text-amber-800 border-amber-200',
          icon: '⚠️',
          label: 'Important',
        };
      default:
        return null;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileType: string): string => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('text')) return '📃';
    return '📎';
  };

  const handleDownload = (url: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getReactionCount = (reactions: PostReactions, emoji: string): number => {
    const emojiMap: { [key: string]: keyof PostReactions } = {
      '👍': 'thumbsUp',
      '❤️': 'heart',
      '🎉': 'party',
      '👀': 'eyes',
    };
    const key = emojiMap[emoji];
    return reactions?.[key]?.length || 0;
  };

  const hasUserReacted = (reactions: PostReactions, emoji: string, userId: string): boolean => {
    const emojiMap: { [key: string]: keyof PostReactions } = {
      '👍': 'thumbsUp',
      '❤️': 'heart',
      '🎉': 'party',
      '👀': 'eyes',
    };
    const key = emojiMap[emoji];
    return reactions?.[key]?.includes(userId) || false;
  };

  const handleReaction = async (emoji: string, currentlyReacted: boolean) => {
    try {
      const emojiKey = emoji === '👍' ? 'thumbsUp' : emoji === '❤️' ? 'heart' : emoji === '🎉' ? 'party' : 'eyes';
      
      if (currentlyReacted) {
        await removeReaction({
          variables: { postId: post.id, emoji: emojiKey },
        });
      } else {
        await addReaction({
          variables: { postId: post.id, emoji: emojiKey },
        });
      }
      
      if (onReactionUpdate) {
        onReactionUpdate();
      }
    } catch (error: any) {
      console.error('Error updating reaction:', error);
      toast.error('Failed to update reaction');
    }
  };

  const handleDelete = async () => {
    try {
      await deletePost({
        variables: { id: post.id },
      });
      setShowDeleteConfirm(false);
      toast.success('Post deleted successfully');
      onPostDeleted();
    } catch (error: any) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post: ' + error.message);
    }
  };

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const priorityBadge = getPriorityBadge(post.priority);
  
  // Combine attachment images and legacy images for lightbox
  const lightboxSlides = [
    ...(post.attachments?.filter(a => a.isImage).map(a => ({ src: a.url })) || []),
    ...(post.images?.map(img => ({ src: img.url })) || [])
  ];

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-aalaw-charcoal">Post Details</h3>
              {post.isPinned && (
                <span className="text-aalaw-red text-lg" title="Pinned post">
                  📌
                </span>
              )}
              {priorityBadge && (
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${priorityBadge.color}`}
                >
                  <span>{priorityBadge.icon}</span>
                  {priorityBadge.label}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1">
            <div className="space-y-4">
              {/* Metadata */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">
                  {formatTimestamp(post.createdAt)}
                </p>
                <p className="text-base font-semibold text-gray-900">
                  Posted by {post.creator.firstName} {post.creator.lastName}
                </p>
                {post.updatedAt !== post.createdAt && (
                  <p className="text-xs text-gray-500 mt-1">
                    Updated on {formatTimestamp(post.updatedAt)}
                  </p>
                )}
              </div>

              {/* Content with Markdown Rendering */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 prose prose-sm max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    code({ className, children, ...props }) {
                      const match = /language-(\w+)/.exec(className || '');
                      const isInline = !match;
                      return isInline ? (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      ) : (
                        // @ts-ignore - SyntaxHighlighter type issue
                        <SyntaxHighlighter
                          style={vscDarkPlus as any}
                          language={match[1]}
                          PreTag="div"
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      );
                    },
                  }}
                >
                  {post.content}
                </ReactMarkdown>
              </div>

              {/* Attachments Section */}
              {((post.attachments && post.attachments.length > 0) || (post.images && post.images.length > 0)) && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Attachments</h4>
                  
                  {/* Image Attachments */}
                  {post.attachments && post.attachments.filter(a => a.isImage).length > 0 && (
                    <div className="mb-4">
                      <div className="grid grid-cols-3 gap-3">
                        {post.attachments.filter(a => a.isImage).map((attachment) => (
                          <div
                            key={attachment.id}
                            className="relative group cursor-pointer"
                            onClick={() => {
                              const imageIndex = post.attachments!.filter(a => a.isImage).findIndex(a => a.id === attachment.id);
                              openLightbox(imageIndex);
                            }}
                          >
                            <img
                              src={attachment.url}
                              alt={attachment.fileName}
                              className="w-full h-32 object-cover rounded-lg group-hover:opacity-80 transition-opacity"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center">
                              <svg
                                className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                />
                              </svg>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg truncate">
                              {attachment.fileName}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Legacy Images (for backward compatibility) */}
                  {post.images && post.images.length > 0 && (
                    <div className="mb-4">
                      <div className="grid grid-cols-3 gap-3">
                        {post.images.map((image, index) => (
                          <div
                            key={image.id}
                            className="relative group cursor-pointer"
                            onClick={() => {
                              const offset = post.attachments?.filter(a => a.isImage).length || 0;
                              openLightbox(offset + index);
                            }}
                          >
                            <img
                              src={image.thumbnailUrl || image.url}
                              alt={`Image ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg group-hover:opacity-80 transition-opacity"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center">
                              <svg
                                className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                />
                              </svg>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Document Attachments */}
                  {post.attachments && post.attachments.filter(a => !a.isImage).length > 0 && (
                    <div className="space-y-2">
                      {post.attachments.filter(a => !a.isImage).map((attachment) => (
                        <div
                          key={attachment.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span className="text-2xl flex-shrink-0">{getFileIcon(attachment.fileType)}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {attachment.fileName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(attachment.fileSize)}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDownload(attachment.url, attachment.fileName)}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-aalaw-red hover:bg-aalaw-red hover:text-white rounded-md transition-colors flex-shrink-0"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Reaction Bar */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Reactions</h4>
                <div className="flex items-center gap-2 flex-wrap">
                  {['👍', '❤️', '🎉', '👀'].map((emoji) => {
                    const count = getReactionCount(post.reactions, emoji);
                    const hasReacted = hasUserReacted(post.reactions, emoji, currentUserId || '');
                    
                    return (
                      <button
                        key={emoji}
                        onClick={() => handleReaction(emoji, hasReacted)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm transition-all ${
                          hasReacted
                            ? 'bg-aalaw-red/10 border-2 border-aalaw-red text-aalaw-red'
                            : 'bg-gray-100 border-2 border-transparent hover:bg-gray-200'
                        }`}
                      >
                        <span className="text-lg">{emoji}</span>
                        {count > 0 && <span className="text-xs font-medium">{count}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Audience */}
              <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAudienceBadgeColor(
                    post.audienceType
                  )}`}
                >
                  {formatAudienceType(post.audienceType)}
                </span>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>

            {(canEdit || canDelete) && (
              <div className="flex gap-3">
                {canEdit && (
                  <Button
                    variant="outline"
                    onClick={() => setShowEditModal(true)}
                    className="border-aalaw-red text-aalaw-red hover:bg-red-50"
                  >
                    Edit
                  </Button>
                )}
                {canDelete && (
                  <Button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Delete
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox for Images */}
      {lightboxOpen && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={lightboxSlides}
          index={lightboxIndex}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <EditPostModal
          post={post}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onPostUpdated={() => {
            setShowEditModal(false);
            onPostUpdated();
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Post</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this post? This action cannot be undone and the post will be
              removed from all users' dashboards.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? 'Deleting...' : 'Delete Post'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
